export interface Initiative {
  id: string;
  name: string;
  category: string;
  costRange: {
    min: number;
    max: number;
  };
  metrics: {
    name: string;
    weight: number;
    impact: number; // 1-10 scale
  }[];
  timeToImpact: "short" | "medium" | "long"; // How quickly results would appear
  difficulty: "low" | "medium" | "high"; // Implementation complexity
  description: string;
  fullDescription?: string; // More detailed explanation 
  interdependencies?: string[];
  caseStudies?: {
    university: string;
    outcome: string;
    link?: string;
  }[];
}

// Define categories
export const CATEGORIES = [
  "Outcomes",
  "Expert Opinion",
  "Student Excellence",
  "Faculty Research",
  "Infrastructure",
  "Technology"
] as const;

export type Category = typeof CATEGORIES[number];

// Define the remapping function
const remapInitiativeCategories = (initiatives: Initiative[]): Initiative[] => {
  return initiatives.map(initiative => {
    // Map 'Career' and 'Student Retention' to 'Outcomes'
    if (initiative.category === "Career" || initiative.category === "Student Retention") {
      return {
        ...initiative,
        category: "Outcomes"
      };
    }
    // Map 'Enrollment' or 'Reputation' to 'Expert Opinion'
    else if (initiative.category === "Enrollment" || initiative.category === "Reputation") {
      return {
        ...initiative,
        category: "Expert Opinion"
      };
    }
    // Map 'New Programs' or 'New Programs & Modalities' to 'Student Excellence'
    else if (initiative.category === "New Programs" || initiative.category === "New Programs & Modalities") {
      return {
        ...initiative,
        category: "Student Excellence"
      };
    }
    return initiative;
  });
};

// Define and export the initiatives array
export const INITIATIVES: Initiative[] = remapInitiativeCategories([
  // Student Retention Initiatives
  {
    id: "dcob_advising_gr",
    name: "Academic Support & Advising (Graduate)",
    category: "Student Retention",
    costRange: {
      min: 400000,
      max: 500000
    },
    metrics: [
      { name: "Graduation Rates", weight: 16, impact: 7 },
      { name: "First-Year Retention", weight: 5, impact: 8 }
    ],
    timeToImpact: "medium",
    difficulty: "medium",
    description: "Enhance graduate student advising services to improve retention and graduation rates.",
    fullDescription: "Implement comprehensive academic advising for graduate students with dedicated advisors, regular check-ins, and personalized academic planning. Focus on early identification of at-risk students and proactive intervention strategies.",
    interdependencies: ["Outcomes"]
  },
  {
    id: "dcob_advising_ug",
    name: "Academic Support & Advising (Undergraduate)",
    category: "Student Retention",
    costRange: {
      min: 800000,
      max: 1000000
    },
    metrics: [
      { name: "Graduation Rates", weight: 16, impact: 8 },
      { name: "First-Year Retention", weight: 5, impact: 9 }
    ],
    timeToImpact: "medium",
    difficulty: "medium",
    description: "Enhance undergraduate student advising services to improve retention and graduation rates.",
    fullDescription: "Develop a comprehensive undergraduate advising program with dedicated advisors for each major, regular academic progress monitoring, and integrated career planning. Includes specialized support for first-generation and at-risk students."
  },
  {
    id: "dcob_tutoring",
    name: "Tutoring Center Enhancement",
    category: "Student Retention",
    costRange: {
      min: 1200000,
      max: 1400000
    },
    metrics: [
      { name: "Graduation Rates", weight: 16, impact: 7 },
      { name: "First-Year Retention", weight: 5, impact: 8 }
    ],
    timeToImpact: "short",
    difficulty: "low",
    description: "Expand tutoring services with peer tutors, supplemental instruction, and extended hours.",
    fullDescription: "Create a comprehensive tutoring center with trained peer tutors, faculty-led review sessions, and supplemental instruction for challenging gateway courses. Includes both in-person and virtual tutoring options to maximize accessibility."
  },
  {
    id: "dcob_student_engagement",
    name: "Student Engagement Initiatives",
    category: "Student Retention",
    costRange: {
      min: 50000,
      max: 100000
    },
    metrics: [
      { name: "First-Year Retention", weight: 5, impact: 6 }
    ],
    timeToImpact: "short",
    difficulty: "low",
    description: "Modernize student engagement programs to increase sense of belonging and community.",
    fullDescription: "Implement a series of student engagement initiatives including learning communities, student organizations, networking events, and mentorship programs to foster a sense of belonging and community among students."
  },
  {
    id: "las_cluster_hire",
    name: "LSP Cluster Faculty Hire",
    category: "Faculty Research",
    costRange: {
      min: 4000000,
      max: 5000000
    },
    metrics: [
      { name: "Faculty Resources", weight: 8, impact: 8 },
      { name: "Class Size", weight: 4, impact: 7 }
    ],
    timeToImpact: "long",
    difficulty: "high",
    description: "Strategic hiring of faculty clusters in Liberal Studies Program to enhance teaching quality.",
    fullDescription: "Implement a strategic cluster hiring initiative for the Liberal Studies Program, bringing in groups of faculty with complementary expertise to strengthen core curriculum delivery and reduce class sizes in foundational courses."
  },
  {
    id: "las_advisors",
    name: "LAS Academic Advisors",
    category: "Faculty Research",
    costRange: {
      min: 600000,
      max: 800000
    },
    metrics: [
      { name: "Graduation Rates", weight: 16, impact: 7 },
      { name: "First-Year Retention", weight: 5, impact: 6 }
    ],
    timeToImpact: "medium",
    difficulty: "medium",
    description: "Increase number of academic advisors in LAS to improve student support.",
    fullDescription: "Recruit additional academic advisors for the College of Liberal Arts and Social Sciences to reduce advisor-to-student ratios and provide more personalized academic guidance, particularly for first-generation and at-risk students."
  },
  
  // Career Initiatives
  {
    id: "dcob_future_forward",
    name: "Future Forward Career Program",
    category: "Career",
    costRange: {
      min: 300000,
      max: 400000
    },
    metrics: [
      { name: "Graduate Income", weight: 8, impact: 7 },
      { name: "Graduation Rates", weight: 16, impact: 5 }
    ],
    timeToImpact: "medium",
    difficulty: "medium",
    description: "Comprehensive career readiness program focused on future skills and industry connections.",
    fullDescription: "Implement a forward-looking career preparation program that focuses on emerging industry trends, future skills development, and strategic employer partnerships to position students for high-growth career opportunities."
  },
  {
    id: "dcob_coop",
    name: "Cooperative Education Program",
    category: "Career",
    costRange: {
      min: 900000,
      max: 1100000
    },
    metrics: [
      { name: "Graduate Income", weight: 8, impact: 9 },
      { name: "Graduation Rates", weight: 16, impact: 6 }
    ],
    timeToImpact: "medium",
    difficulty: "high",
    description: "Establish structured cooperative education program with paid industry placements.",
    fullDescription: "Develop a comprehensive cooperative education program that integrates paid work experience with academic studies, allowing students to alternate between academic terms and full-time employment related to their field of study."
  },
  {
    id: "dcob_career_management",
    name: "Kellstadt Career Management Center",
    category: "Career",
    costRange: {
      min: 700000,
      max: 900000
    },
    metrics: [
      { name: "Graduate Income", weight: 8, impact: 8 },
      { name: "Peer Assessment", weight: 20, impact: 4 }
    ],
    timeToImpact: "medium",
    difficulty: "medium",
    description: "Enhance career services specifically for business students with industry partnerships.",
    fullDescription: "Establish a dedicated career management center for business students with specialized career coaches, industry-specific programming, executive mentorship opportunities, and enhanced employer relations to improve placement rates and starting salaries."
  },
  {
    id: "dcob_career_tools",
    name: "Career Support Technology",
    category: "Career",
    costRange: {
      min: 50000,
      max: 80000
    },
    metrics: [
      { name: "Graduate Income", weight: 8, impact: 5 }
    ],
    timeToImpact: "short",
    difficulty: "low",
    description: "Implement advanced career management software and tools for students and employers.",
    fullDescription: "Invest in cutting-edge career management technology including AI-powered resume review, interview practice platforms, job matching algorithms, and comprehensive career assessment tools to enhance student career preparation and employer connections."
  },
  {
    id: "col_tutoring",
    name: "Law School Tutoring Center",
    category: "Career",
    costRange: {
      min: 40000,
      max: 60000
    },
    metrics: [
      { name: "Graduation Rates", weight: 16, impact: 6 },
      { name: "Graduate Income", weight: 8, impact: 5 }
    ],
    timeToImpact: "short",
    difficulty: "low",
    description: "Create a tutoring center for first-year law students to improve Bar passage rates.",
    fullDescription: "Establish a dedicated tutoring center for first-year law students with peer tutors, faculty support, and specialized resources focused on core first-year courses that are critical for building the foundation for Bar exam success."
  },
  {
    id: "col_bar_passage",
    name: "Bar Passage Improvement Program",
    category: "Career",
    costRange: {
      min: 200000,
      max: 300000
    },
    metrics: [
      { name: "Graduate Income", weight: 8, impact: 7 },
      { name: "Peer Assessment", weight: 20, impact: 5 }
    ],
    timeToImpact: "medium",
    difficulty: "medium",
    description: "Comprehensive program to improve first-time Illinois Bar passage performance.",
    fullDescription: "Implement a multi-faceted Bar passage program including specialized Bar preparation courses, individualized coaching, practice exam workshops, and data-driven interventions to continuously improve first-time Bar passage rates."
  },
  
  // Enrollment Initiatives
  {
    id: "dcob_grad_marketing",
    name: "Graduate Program Marketing",
    category: "Expert Opinion",
    costRange: {
      min: 8000000,
      max: 12000000
    },
    metrics: [
      { name: "Student Quality", weight: 7, impact: 8 },
      { name: "Peer Assessment", weight: 20, impact: 6 }
    ],
    timeToImpact: "medium",
    difficulty: "high",
    description: "Reposition and rebrand graduate business programs with targeted marketing.",
    fullDescription: "Launch a comprehensive marketing campaign to reposition and rebrand graduate business programs, highlighting distinctive program features, career outcomes, and faculty expertise to attract high-quality applicants and improve program reputation."
  },
  {
    id: "las_blue_book",
    name: "DePaul Blue Book",
    category: "Expert Opinion",
    costRange: {
      min: 400000,
      max: 600000
    },
    metrics: [
      { name: "Student Quality", weight: 7, impact: 6 },
      { name: "Application Volume", weight: 1, impact: 7 }
    ],
    timeToImpact: "medium",
    difficulty: "medium",
    description: "Create a distinctive publication showcasing academic programs and student outcomes.",
    fullDescription: "Develop a premium publication highlighting DePaul's academic programs, student achievements, faculty research, and alumni success stories to distribute to prospective students, high school counselors, and other key stakeholders."
  },
  {
    id: "las_tedx",
    name: "DePaul Alumni TedX",
    category: "Expert Opinion",
    costRange: {
      min: 150000,
      max: 200000
    },
    metrics: [
      { name: "Peer Assessment", weight: 20, impact: 5 },
      { name: "Application Volume", weight: 1, impact: 6 }
    ],
    timeToImpact: "short",
    difficulty: "medium",
    description: "Organize TedX events featuring distinguished alumni to enhance visibility.",
    fullDescription: "Establish an annual TedX event series featuring accomplished DePaul alumni sharing innovative ideas and success stories, creating high-quality content for marketing and enhancing the university's reputation and visibility."
  },
  {
    id: "cmn_recruitment",
    name: "Communication Recruitment Initiatives",
    category: "Expert Opinion",
    costRange: {
      min: 400000,
      max: 600000
    },
    metrics: [
      { name: "Student Quality", weight: 7, impact: 7 },
      { name: "Application Volume", weight: 1, impact: 8 }
    ],
    timeToImpact: "medium",
    difficulty: "medium",
    description: "Targeted recruitment strategies for communication programs.",
    fullDescription: "Implement specialized recruitment initiatives for communication programs including high school workshops, media competitions, summer academies, and strategic partnerships with media organizations to attract talented students."
  },
  
  // New Programs & Modalities Initiatives
  {
    id: "tts_certificates",
    name: "New Certificate Programs",
    category: "Student Excellence",
    costRange: {
      min: 300000,
      max: 500000
    },
    metrics: [
      { name: "Student Quality", weight: 7, impact: 6 },
      { name: "Application Volume", weight: 1, impact: 7 }
    ],
    timeToImpact: "medium",
    difficulty: "medium",
    description: "Develop new certificate programs in high-demand fields.",
    fullDescription: "Design and implement new certificate programs in emerging or high-demand fields, including data science, artificial intelligence, sustainability, healthcare management, and cybersecurity, providing flexible pathways for career advancement or transition."
  },
  {
    id: "cmn_la_quarter",
    name: "LA Quarter Program",
    category: "Student Excellence",
    costRange: {
      min: 200000,
      max: 400000
    },
    metrics: [
      { name: "Student Quality", weight: 7, impact: 7 },
      { name: "Application Volume", weight: 1, impact: 8 }
    ],
    timeToImpact: "medium",
    difficulty: "medium",
    description: "Establish LA-based industry immersion program.",
    fullDescription: "Create an LA Quarter program offering students immersive learning experiences in the entertainment industry through coursework, internships, and networking opportunities with industry professionals in Los Angeles."
  },
  {
    id: "csh_studyaway",
    name: "StudyAway Experiential Learning",
    category: "Student Excellence",
    costRange: {
      min: 600000,
      max: 800000
    },
    metrics: [
      { name: "Student Quality", weight: 7, impact: 6 },
      { name: "Application Volume", weight: 1, impact: 7 },
      { name: "First-Year Retention", weight: 5, impact: 5 }
    ],
    timeToImpact: "medium",
    difficulty: "medium",
    description: "Expand StudyAway programs with experiential learning components.",
    fullDescription: "Expand StudyAway programs that combine domestic experiential learning with academic content, focusing on environmental science, sustainable development, public health, and social justice in diverse U.S. locations."
  },
  {
    id: "cdm_cloud",
    name: "High Performance Computing Cloud",
    category: "Student Excellence",
    costRange: {
      min: 700000,
      max: 900000
    },
    metrics: [
      { name: "Faculty Research Output", weight: 2.5, impact: 8 },
      { name: "Citations per Publication", weight: 1.25, impact: 7 }
    ],
    timeToImpact: "medium",
    difficulty: "high",
    description: "Implement high-performance computing cloud for research and education.",
    fullDescription: "Develop a high-performance computing cloud infrastructure to support advanced research and education in artificial intelligence, data science, computational science, and other computing-intensive fields across the university."
  },
  {
    id: "cdm_flex",
    name: "FLEX Online Program Enhancement",
    category: "Student Excellence",
    costRange: {
      min: 1500000,
      max: 2500000
    },
    metrics: [
      { name: "Student Quality", weight: 7, impact: 7 },
      { name: "Application Volume", weight: 1, impact: 9 }
    ],
    timeToImpact: "medium",
    difficulty: "high",
    description: "Expand online program offerings through the FLEX platform.",
    fullDescription: "Significantly expand and enhance online program offerings through the FLEX platform, leveraging high-performance computing cloud resources and developing high-quality asynchronous courses with interactive elements and industry-relevant projects."
  },
  
  // Faculty Resources Initiatives
  {
    id: "faculty_fellowship",
    name: "Faculty Fellowship Program",
    category: "Faculty Research",
    costRange: {
      min: 1500000,
      max: 2000000
    },
    metrics: [
      { name: "Faculty Research Output", weight: 2.5, impact: 9 },
      { name: "Citations per Publication", weight: 1.25, impact: 8 }
    ],
    timeToImpact: "long",
    difficulty: "medium",
    description: "Establish prestigious fellowship program to attract and retain top faculty.",
    fullDescription: "Create a prestigious university-wide faculty fellowship program to attract and retain distinguished scholars, providing funding for research, reduced teaching loads, and opportunities for interdisciplinary collaboration and mentorship of junior faculty."
  },
  {
    id: "endowed_chair",
    name: "Endowed Chair Positions",
    category: "Faculty Research",
    costRange: {
      min: 8000000,
      max: 12000000
    },
    metrics: [
      { name: "Faculty Research Output", weight: 2.5, impact: 8 },
      { name: "Citations per Publication", weight: 1.25, impact: 9 },
      { name: "Peer Assessment", weight: 20, impact: 7 }
    ],
    timeToImpact: "long",
    difficulty: "medium",
    description: "Establish endowed chair positions to attract distinguished faculty.",
    fullDescription: "Establish multiple endowed chair positions across colleges to attract distinguished senior faculty with exceptional records of scholarship, teaching, and leadership, enhancing the university's academic reputation and research profile."
  },
  {
    id: "faculty_dev",
    name: "Faculty Development Programs",
    category: "Faculty Research",
    costRange: {
      min: 800000,
      max: 1200000
    },
    metrics: [
      { name: "Faculty Research Output", weight: 2.5, impact: 7 },
      { name: "Peer Assessment", weight: 20, impact: 5 }
    ],
    timeToImpact: "medium",
    difficulty: "low",
    description: "Enhance faculty development programs to support teaching and research excellence.",
    fullDescription: "Expand faculty development programs offering workshops, mentoring, and resources to support teaching innovation, research productivity, grant writing, and professional advancement for faculty at all career stages."
  },
  {
    id: "adjunct_support",
    name: "Adjunct Faculty Support Initiative",
    category: "Faculty Research",
    costRange: {
      min: 500000,
      max: 700000
    },
    metrics: [
      { name: "Graduation Rates", weight: 16, impact: 5 },
      { name: "First-Year Retention", weight: 5, impact: 6 }
    ],
    timeToImpact: "short",
    difficulty: "low",
    description: "Enhance support and resources for adjunct faculty to improve teaching quality.",
    fullDescription: "Develop a comprehensive support system for adjunct faculty, including orientation programs, mentoring, professional development, dedicated workspace, and improved compensation, to enhance teaching quality and institutional engagement."
  },
  
  // Infrastructure Initiatives
  {
    id: "campus_planning",
    name: "Campus Master Planning",
    category: "Infrastructure",
    costRange: {
      min: 2500000,
      max: 3500000
    },
    metrics: [
      { name: "Peer Assessment", weight: 20, impact: 6 },
      { name: "Financial Resources", weight: 8, impact: 5 }
    ],
    timeToImpact: "long",
    difficulty: "high",
    description: "Develop comprehensive campus master plan for future development.",
    fullDescription: "Create a comprehensive campus master plan that guides future campus development, space utilization, facility renovations, and sustainability initiatives to enhance the student experience and support academic excellence."
  },
  {
    id: "safety_security",
    name: "Campus Safety & Security Enhancement",
    category: "Infrastructure",
    costRange: {
      min: 8000000,
      max: 12000000
    },
    metrics: [
      { name: "Peer Assessment", weight: 20, impact: 5 },
      { name: "First-Year Retention", weight: 5, impact: 6 }
    ],
    timeToImpact: "medium",
    difficulty: "high",
    description: "Comprehensive enhancement of campus safety and security systems.",
    fullDescription: "Implement comprehensive safety and security enhancements including upgraded surveillance systems, emergency notification infrastructure, access control technologies, and expanded public safety personnel to create a safer campus environment."
  },
  
  // Technology Initiatives
  {
    id: "data_infrastructure",
    name: "Enhanced Data Infrastructure",
    category: "Technology",
    costRange: {
      min: 2800000,
      max: 3600000
    },
    metrics: [
      { name: "Financial Resources", weight: 8, impact: 6 },
      { name: "Graduation Rates", weight: 16, impact: 5 }
    ],
    timeToImpact: "medium",
    difficulty: "high",
    description: "Enhance data infrastructure for improved analytics and decision-making.",
    fullDescription: "Develop a comprehensive data infrastructure that integrates information across university systems, enables advanced analytics, and supports data-driven decision-making for student success, enrollment management, and institutional effectiveness."
  },
  {
    id: "salesforce_education",
    name: "Salesforce Education Cloud Implementation",
    category: "Technology",
    costRange: {
      min: 3000000,
      max: 4000000
    },
    metrics: [
      { name: "First-Year Retention", weight: 5, impact: 7 },
      { name: "Graduation Rates", weight: 16, impact: 6 }
    ],
    timeToImpact: "medium",
    difficulty: "high",
    description: "Implement Salesforce Education Cloud for improved student lifecycle management.",
    fullDescription: "Implement Salesforce Education Cloud to create a unified platform for managing the entire student lifecycle from prospect to alumni, enabling personalized communication, streamlined processes, and improved student support services."
  },
  {
    id: "security_operations",
    name: "Security Operations Center",
    category: "Technology",
    costRange: {
      min: 2800000,
      max: 3500000
    },
    metrics: [
      { name: "Financial Resources", weight: 8, impact: 5 }
    ],
    timeToImpact: "medium",
    difficulty: "high",
    description: "Establish dedicated security operations center to protect university systems.",
    fullDescription: "Create a dedicated security operations center with specialized staff, advanced monitoring tools, and incident response capabilities to protect university systems and data from cyber threats and ensure regulatory compliance."
  },
  {
    id: "high_performance_computing",
    name: "High Performance Computing Initiative",
    category: "Technology",
    costRange: {
      min: 7000000,
      max: 9000000
    },
    metrics: [
      { name: "Faculty Research Output", weight: 2.5, impact: 9 },
      { name: "Citations per Publication", weight: 1.25, impact: 8 }
    ],
    timeToImpact: "long",
    difficulty: "high",
    description: "Establish high-performance computing infrastructure for research and education.",
    fullDescription: "Develop a comprehensive high-performance computing infrastructure to support advanced research, data science education, and computational applications across disciplines, positioning the university as a leader in computational research and education."
  },
  {
    id: "branding",
    name: "University Branding Initiative",
    category: "Expert Opinion",
    costRange: {
      min: 5000000,
      max: 7000000
    },
    metrics: [
      { name: "Peer Assessment", weight: 20, impact: 9 }
    ],
    timeToImpact: "long",
    difficulty: "high",
    description: "Comprehensive university branding and marketing campaign.",
    fullDescription: "Launch a comprehensive university-wide branding and marketing initiative to elevate DePaul's national profile, highlighting distinctive academic programs, research achievements, and Vincentian mission. Includes digital marketing, traditional media, and strategic partnerships."
  },
  {
    id: "athletics",
    name: "Athletics Visibility Enhancement",
    category: "Expert Opinion",
    costRange: {
      min: 12000000,
      max: 18000000
    },
    metrics: [
      { name: "Peer Assessment", weight: 20, impact: 8 },
      { name: "Application Volume", weight: 1, impact: 7 }
    ],
    timeToImpact: "medium",
    difficulty: "high",
    description: "Enhance athletics program visibility and competitiveness.",
    fullDescription: "Significantly increase investment in athletics programs to enhance visibility and competitiveness, including funding for current operations, NIL programs, roster expansion, and revenue growth initiatives through ticket sales and sponsorships."
  }
]); 